import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { Checkbox } from 'primereact/checkbox';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CalendarEventCard } from '@components/ringil3/CalendarEventCard/CalendarEventCard';
import { CalendarEventCardDetail } from '@components/ringil3/CalendarEventCardDetail/CalendarEventCardDetail';
import { CalendarFragmentKey, CalendarLayoutConfig, CalendarSurface, LayoutZone, SurfaceLayout, zoneItems, zonesForSurface } from '../model';
import { findFragment } from '../fragments/registry';
import { MOCK_RESERVATION, previewReservation } from '../preview/mockReservation';
import { resolveStateStyle } from '../stateStyle';
import {
  CalendarDensity,
  cardHeightForMinutes,
  estimateCardHeight,
  insertFragment,
  moveFragment,
  normalizeSurface,
  removeFragment,
  unplacedFragments,
  updateFragment,
} from './layoutOps';
import { DropTarget, useLayoutDnd } from './useLayoutDnd';

const ZONE_LABELS: Record<LayoutZone, { key: string; fallback: string }> = {
  [LayoutZone.HeaderLeft]: { key: 'CalendarLayout.zone.headerLeft', fallback: 'Header — left' },
  [LayoutZone.HeaderRight]: { key: 'CalendarLayout.zone.headerRight', fallback: 'Header — right' },
  [LayoutZone.Body]: { key: 'CalendarLayout.zone.body', fallback: 'Body' },
};

/**
 * How a zone's fragments flow in the real component, which decides both how the edit wrapper lays
 * them out and which way a drop indicator is drawn.
 *
 * Only the card's header sides are inline rows; the popover stacks every zone, its header sides
 * included.
 */
const isHorizontalZone = (surface: CalendarSurface, zone: LayoutZone) => surface === 'card' && (zone === LayoutZone.HeaderLeft || zone === LayoutZone.HeaderRight);

/**
 * The gap the surface's own container puts between fragments in a zone. The edit wrapper has to
 * reapply it: it inserts itself between that container and the fragments, so a `gap-*` on the
 * container would otherwise only ever see one child and collapse.
 */
const zoneFlowClass = (surface: CalendarSurface, zone: LayoutZone): string => {
  if (isHorizontalZone(surface, zone)) return 'inline-flex items-center min-w-0';
  // `gap-y-4` on the popover's body — see CalendarEventCardDetail.
  if (surface === 'detail' && zone === LayoutZone.Body) return 'flex flex-col gap-y-4';
  // `gap-y-1.5` on the card's body — see CalendarEventCard.
  if (surface === 'card' && zone === LayoutZone.Body) return 'flex flex-col gap-y-1.5';
  return 'block';
};

/** Approximate width FullCalendar gives an event card in a ramp column — it varies with ramp count. */
const CARD_WIDTH = 260;

/** The popover's fixed width: `w-[21.25rem]` in CalendarEventCardDetail. */
const DETAIL_WIDTH = 340;

/** Reservation lengths worth checking a layout against. */
const DURATION_OPTIONS = [15, 30, 45, 60, 90];

/**
 * Both edit surfaces are drawn to this on-screen width, so switching between the card and the
 * popover does not change how big the thing you are editing is. The popover is the wider component
 * of the two, so it ends up magnified less than the card rather than dwarfing it.
 */
const EDIT_SURFACE_WIDTH = 460;

/**
 * The editing surfaces are magnified so drag targets are big enough to hit precisely, while keeping
 * the exact proportions and wrapping the components have in the calendar.
 *
 * Applied with `zoom` rather than `transform: scale()` deliberately. A transform does not affect
 * layout, so the wrapper has to be told how much room to reserve — which means measuring the content
 * and always being one frame behind it, so every time a fragment was added the card briefly spilled
 * over the previews below. `zoom` participates in layout, so the box simply grows with its content
 * and nothing can overlap. Hit testing and pointer coordinates scale correctly either way, so the
 * drop-indicator maths is unaffected.
 *
 * (Firefox below 126 ignores `zoom`; there the edit surface renders at actual size, which is a
 * cosmetic degradation, not a broken editor.)
 */
const editScaleFor = (width: number) => EDIT_SURFACE_WIDTH / width;

interface CalendarLayoutEditorProps {
  /** The layout being edited. Controlled: every change is reported through `onChange`. */
  value: CalendarLayoutConfig;
  onChange: (next: CalendarLayoutConfig) => void;
  /**
   * Which surface is on screen. Controlled by the owner because the two surfaces are separate tabs
   * of the model screen, not a switch inside the editor.
   */
  surface: CalendarSurface;
  /** Read-only mode for a model the caller may look at but not edit (a system-wide one, for a customer). */
  readOnly?: boolean;
}

/**
 * The drag-and-drop layout designer.
 *
 * Deliberately controlled and storage-agnostic: it knows how to edit a `CalendarLayoutConfig` and
 * nothing about models, drafts or the API. The screens that own a model decide what saving means.
 */
export const CalendarLayoutEditor: React.FC<CalendarLayoutEditorProps> = ({ value, onChange, surface, readOnly }) => {
  const { tr } = useTranslationLgs();

  const [selected, setSelected] = useState<CalendarFragmentKey | null>(null);
  const [addToZone, setAddToZone] = useState<LayoutZone | null>(null);
  const [slotMinutes, setSlotMinutes] = useState(30);

  const dnd = useLayoutDnd();

  // Normalised on the way in rather than on every edit: a payload from the API (or from the import
  // box) may hold zones this surface does not render, and the editor must never show them.
  const draft = useMemo<CalendarLayoutConfig>(
    () => ({ ...value, card: normalizeSurface(value.card, 'card'), detail: normalizeSurface(value.detail, 'detail') }),
    [value],
  );

  const currentLayout = surface === 'card' ? draft.card : draft.detail;

  const applyLayout = (next: SurfaceLayout) => {
    if (readOnly) return;
    onChange(surface === 'card' ? { ...draft, card: next } : { ...draft, detail: next });
  };

  const handleDrop = (key: CalendarFragmentKey, target: DropTarget) => {
    applyLayout(insertFragment(currentLayout, target.zone, key, target.index < 0 ? undefined : target.index));
    setSelected(key);
  };

  const available = useMemo(() => unplacedFragments(currentLayout, surface), [currentLayout, surface]);
  // The edit card is deliberately fixed at the base reservation: the length selector is about
  // checking how the layout survives a short slot, and changing the card you are editing underneath
  // you (its padding tightens under 30 minutes) makes it harder to work with, not easier.
  const editData = MOCK_RESERVATION;
  // The Result cards are length-aware, so they apply the same short-slot styling as the calendar.
  const resultData = useMemo(() => previewReservation(slotMinutes), [slotMinutes]);
  const stateStyle = resolveStateStyle(editData.state);

  // The calendar runs at two densities, so both are previewed rather than hidden behind a toggle.
  const estimatedHeight = estimateCardHeight(draft.card);
  const condensedHeight = cardHeightForMinutes(slotMinutes, 'condensed');
  const extendedHeight = cardHeightForMinutes(slotMinutes, 'extended');
  const clippedIn = (
    [
      ['condensed', condensedHeight],
      ['extended', extendedHeight],
    ] as [CalendarDensity, number][]
  )
    .filter(([, height]) => estimatedHeight > height)
    .map(([name]) => name);

  // ---- edit affordances injected into the real card / detail components -------------------------

  const zoneWrapper = (zone: LayoutZone, node: React.ReactNode) => {
    const items = zoneItems(currentLayout, zone);
    const horizontal = isHorizontalZone(surface, zone);
    const isDropZone = dnd.dropTarget?.zone === zone;
    return (
      // The outline, tint and labelling are what make the drop areas legible, but they are drawn so
      // they cost as little layout as possible: `outline` (unlike `border`) sits outside the box
      // model, so the zone occupies exactly the space its fragments do and the edit surface stays
      // close to the pixel-true Result previews below. Only the label strip at the top is real
      // space, and it is the minimum the 6px caption needs.
      <div
        key={zone}
        onDragOver={event => dnd.hoverZone(event, zone, items.length)}
        onDrop={event => dnd.commitDrop(event, zone, handleDrop)}
        className={clsx(
          // No horizontal padding at all: the card is only CARD_WIDTH wide, and every pixel here
          // squeezes the content and makes text truncate earlier than it would in the calendar.
          'relative group/zone rounded outline-dashed outline-1 outline-offset-[-1px] pt-1.5 transition-colors',
          zoneFlowClass(surface, zone),
          // An emptied zone would collapse to nothing and become impossible to drop into.
          items.length === 0 && (horizontal ? 'min-w-10 min-h-4' : 'min-h-4'),
          isDropZone ? 'outline-content-brand bg-content-brand/10' : 'outline-content-tertiary bg-content-brand/[0.04] hover:bg-content-brand/10',
        )}
      >
        {/* Always-visible zone name — the whole point is that the areas are obvious at a glance. */}
        <span className="absolute top-0 left-1 z-20 text-[6px] uppercase tracking-wide leading-none text-content-tertiary pointer-events-none select-none">
          {tr(ZONE_LABELS[zone].key, ZONE_LABELS[zone].fallback)}
        </span>
        {node}
        {items.length === 0 && (
          <span className="text-[7px] italic text-content-tertiary pointer-events-none select-none">{tr('CalendarLayout.dropHere', 'drop here')}</span>
        )}
        {/* Explicit tail target. Aiming past the last fragment used to mean hitting a couple of
            pixels of zone padding; while a drag is in progress this opens up a real strip to drop
            onto, so "put it last" is as easy as any other position. */}
        {dnd.draggedKey && items.length > 0 && (
          <div
            onDragOver={event => dnd.hoverZone(event, zone, items.length)}
            onDrop={event => dnd.commitDrop(event, zone, handleDrop)}
            className={clsx(
              'relative rounded-sm border border-dashed',
              horizontal ? 'inline-block w-4 self-stretch min-h-4 ml-0.5' : 'block h-4 mt-0.5',
              isDropZone && dnd.dropTarget?.index === items.length ? 'border-content-brand bg-content-brand/20' : 'border-content-tertiary/70',
            )}
          />
        )}
        {/* Drop indicator when the pointer is past the last item. */}
        {isDropZone && dnd.dropTarget?.index === items.length && <DropLine horizontal={horizontal} />}
        {!readOnly && (
          <button
            type="button"
            // Stop the click reaching the card, which navigates on click in the real calendar.
            onClick={event => {
              event.stopPropagation();
              setAddToZone(zone);
            }}
            className="absolute -top-1.5 -right-1.5 z-20 flex h-3 w-3 items-center justify-center rounded-full border-0 bg-content-brand text-white text-[8px] leading-none cursor-pointer opacity-60 group-hover/zone:opacity-100"
            title={tr('CalendarLayout.addHere', 'Add attribute here')}
          >
            +
          </button>
        )}
      </div>
    );
  };

  const itemWrapper = (itemConfig: { key: CalendarFragmentKey }, zone: LayoutZone, node: React.ReactNode) => {
    const index = zoneItems(currentLayout, zone).findIndex(item => item.key === itemConfig.key);
    const horizontal = isHorizontalZone(surface, zone);
    const showIndicator = dnd.dropTarget?.zone === zone && dnd.dropTarget?.index === index;
    return (
      <div
        draggable={!readOnly}
        onDragStart={event => dnd.startDrag(event, itemConfig.key)}
        onDragEnd={dnd.endDrag}
        onDragOver={event => dnd.hoverItem(event, zone, index, horizontal)}
        onDrop={event => dnd.commitDrop(event, zone, handleDrop)}
        onClick={event => {
          event.stopPropagation();
          setSelected(itemConfig.key);
        }}
        title={tr('CalendarLayout.dragToMove', 'Drag to move, click to configure')}
        className={clsx(
          'relative cursor-grab rounded-sm bg-base-secondary/70 ring-1 ring-inset transition-colors',
          horizontal ? 'inline-flex items-center min-w-0' : 'block',
          dnd.draggedKey === itemConfig.key && 'opacity-40',
          selected === itemConfig.key ? 'ring-content-brand bg-content-brand/10' : 'ring-content-quaternary hover:ring-content-brand/60',
        )}
      >
        {showIndicator && <DropLine horizontal={horizontal} />}
        {node}
      </div>
    );
  };

  const selectedDefinition = selected ? findFragment(selected) : null;
  const selectedConfig = selected
    ? zonesForSurface(surface)
        .flatMap(zone => zoneItems(currentLayout, zone))
        .find(item => item.key === selected)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-4 items-start">
        {/* Palette — nothing to drag from in read-only mode, so it is dropped entirely. */}
        {!readOnly && (
          <section className="w-64 shrink-0 border border-solid border-content-quaternary rounded-lg p-3 bg-white">
            <h3 className="text-sm font-bold mb-2 mt-0">{tr('CalendarLayout.available', 'Available attributes')}</h3>
            {available.length === 0 && <div className="text-xs text-content-secondary">{tr('CalendarLayout.allPlaced', 'Everything is already placed.')}</div>}
            <div className="flex flex-col gap-1">
              {available.map(definition => (
                <div
                  key={definition.key}
                  draggable
                  onDragStart={event => dnd.startDrag(event, definition.key)}
                  onDragEnd={dnd.endDrag}
                  className="flex items-center gap-2 text-sm px-2 py-1.5 rounded border border-solid border-content-quaternary bg-base-quaternary cursor-grab hover:border-content-brand"
                >
                  <span className="text-content-tertiary select-none">⠿</span>
                  <span className="truncate">{tr(definition.labelKey, definition.labelDefault)}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-content-secondary mt-3 mb-0">
              {tr('CalendarLayout.paletteHint', 'Drag an attribute onto the preview, or use the + button on a zone.')}
            </p>
          </section>
        )}

        {/* Preview */}
        {/* The edit card is drawn at EDIT_SCALE, so the pane can need more width than it is given —
            scroll it rather than letting the card overflow the layout. */}
        <section className="flex-1 min-w-0 overflow-x-auto border border-solid border-content-quaternary rounded-lg p-4 bg-base-quaternary">
          {/* Only the card preview is height-constrained, so the slot selector is card-only — and
              the row is dropped entirely on the detail surface rather than left empty. */}
          {surface === 'card' && (
            <div className="flex flex-row flex-wrap items-center gap-4 mb-4">
              <Segmented
                label={tr('CalendarLayout.reservationLength', 'Reservation length')}
                value={slotMinutes}
                options={DURATION_OPTIONS.map(minutes => ({ value: minutes, label: `${minutes} ${tr('Unit.minutesShort', 'min')}` }))}
                onChange={setSlotMinutes}
              />
            </div>
          )}

          {surface === 'card' ? (
            <>
              <div className="flex flex-col gap-6 items-start">
                <div>
                  <div className="text-xs text-content-secondary mb-2">{tr('CalendarLayout.previewEditable', 'Edit')}</div>
                  {/* Grows with its content — `unconstrained` lets the card size to what it holds,
                      and `zoom` keeps that growth in the layout, so it can never overlap the
                      previews below. Clipping is what the Result cards and the warning show. */}
                  <div style={{ zoom: editScaleFor(CARD_WIDTH), width: `${CARD_WIDTH}px` }}>
                    <CalendarEventCard
                      data={editData}
                      link=""
                      layout={currentLayout}
                      onStateChange={() => undefined}
                      zoneWrapper={zoneWrapper}
                      itemWrapper={itemWrapper}
                      disableDetail
                      unconstrained
                    />
                  </div>
                </div>
                {/* The same component with no edit affordances and no scaling, at both densities the
                    calendar runs at: the drop-target outlines and drag wrappers add DOM that shifts
                    spacing, so these are the pixel-true previews of what will actually render. */}
                <div className="flex flex-row flex-wrap gap-8 items-start">
                  <div>
                    <div className="text-xs text-content-secondary mb-2">
                      {tr('CalendarLayout.previewCondensed', 'Result — normal view ({{height}} px)', { height: condensedHeight })}
                    </div>
                    <div style={{ width: `${CARD_WIDTH}px`, height: `${condensedHeight}px` }}>
                      <CalendarEventCard data={resultData} link="" layout={currentLayout} onStateChange={() => undefined} disableDetail />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-content-secondary mb-2">
                      {tr('CalendarLayout.previewExtended', 'Result — large view ({{height}} px)', { height: extendedHeight })}
                    </div>
                    <div style={{ width: `${CARD_WIDTH}px`, height: `${extendedHeight}px` }}>
                      <CalendarEventCard data={resultData} link="" layout={currentLayout} onStateChange={() => undefined} disableDetail />
                    </div>
                  </div>
                </div>
              </div>
              {clippedIn.length > 0 && (
                // The warning yellow is far too light for text, so it colours a chip instead.
                <div className="mt-3 text-xs text-content-primary bg-complementary-warningLight border border-solid border-complementary-warningMedium rounded px-2 py-1.5">
                  {tr(
                    'CalendarLayout.clipWarning',
                    'This configuration needs about {{needed}} px. A {{minutes}} min reservation gives {{condensed}} px in normal view and {{extended}} px in large view, so it will be clipped in: {{views}}.',
                    {
                      needed: estimatedHeight,
                      minutes: slotMinutes,
                      condensed: condensedHeight,
                      extended: extendedHeight,
                      views: clippedIn
                        .map(name => (name === 'condensed' ? tr('CalendarLayout.viewCondensed', 'normal view') : tr('CalendarLayout.viewExtended', 'large view')))
                        .join(', '),
                    },
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-6 items-start">
              <div>
                <div className="text-xs text-content-secondary mb-2">{tr('CalendarLayout.previewEditable', 'Edit')}</div>
                {/* Magnified to the same on-screen width as the card's edit view, so the two
                    surfaces are comparable in size. The zone labels and drop strips are sized for
                    a magnified surface, so an unscaled edit view renders them illegibly small. */}
                <div style={{ zoom: editScaleFor(DETAIL_WIDTH), width: `${DETAIL_WIDTH}px` }}>
                  <CalendarEventCardDetail
                    data={editData}
                    style={stateStyle}
                    link=""
                    layout={currentLayout}
                    onStateChange={() => undefined}
                    zoneWrapper={zoneWrapper}
                    itemWrapper={itemWrapper}
                    keepEmptyZones
                    inline
                  />
                </div>
              </div>
              <div>
                <div className="text-xs text-content-secondary mb-2">{tr('CalendarLayout.previewResult', 'Result (actual size)')}</div>
                {/* Unscaled and without affordances — the pixel-true popover, as the calendar shows it. */}
                <CalendarEventCardDetail data={editData} style={stateStyle} link="" layout={currentLayout} onStateChange={() => undefined} inline />
              </div>
            </div>
          )}
        </section>

        {/* Selected attribute */}
        <section className={clsx('w-64 shrink-0 border border-solid border-content-quaternary rounded-lg p-3 bg-white', readOnly && 'hidden')}>
          <h3 className="text-sm font-bold mb-2 mt-0">{tr('CalendarLayout.attributeSettings', 'Attribute')}</h3>
          {!selectedDefinition && (
            <div className="text-xs text-content-secondary">{tr('CalendarLayout.selectHint', 'Click an attribute in the preview to configure it.')}</div>
          )}
          {selectedDefinition && selectedConfig && (
            <div className="flex flex-col gap-3">
              <div className="text-sm font-bold">{tr(selectedDefinition.labelKey, selectedDefinition.labelDefault)}</div>
              {selectedDefinition.supportsIcon && (
                <label className="flex flex-row items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={selectedConfig.showIcon !== false}
                    onChange={e => applyLayout(updateFragment(currentLayout, selectedDefinition.key, { showIcon: !!e.checked }))}
                  />
                  <span>{tr('CalendarLayout.showIcon', 'Show icon')}</span>
                </label>
              )}
              <div className="flex flex-row gap-2">
                <RingilButton
                  label={tr('CalendarLayout.moveUp', 'Move up')}
                  mode="table"
                  onClick={() => applyLayout(moveFragment(currentLayout, selectedDefinition.key, -1))}
                />
                <RingilButton
                  label={tr('CalendarLayout.moveDown', 'Move down')}
                  mode="table"
                  onClick={() => applyLayout(moveFragment(currentLayout, selectedDefinition.key, 1))}
                />
              </div>
              <RingilButton
                label={tr('CalendarLayout.remove', 'Remove')}
                mode="outlined-rectangle"
                size="small"
                onClick={() => {
                  applyLayout(removeFragment(currentLayout, selectedDefinition.key));
                  setSelected(null);
                }}
              />
            </div>
          )}
        </section>
      </div>

      {/* + button target picker */}
      <RingilDialog
        visible={addToZone !== null}
        onHide={() => setAddToZone(null)}
        heading={addToZone ? tr('CalendarLayout.addTo', 'Add to {{zone}}', { zone: tr(ZONE_LABELS[addToZone].key, ZONE_LABELS[addToZone].fallback) }) : ''}
      >
        <div className="flex flex-col gap-1 w-[420px] max-h-[60vh] overflow-y-auto">
          {available.length === 0 && <div className="text-sm text-content-secondary">{tr('CalendarLayout.allPlaced', 'Everything is already placed.')}</div>}
          {available.map(definition => (
            <button
              key={definition.key}
              type="button"
              className="text-left text-sm px-3 py-2 rounded border border-solid border-content-quaternary bg-white cursor-pointer hover:border-content-brand"
              onClick={() => {
                applyLayout(insertFragment(currentLayout, addToZone, definition.key));
                setSelected(definition.key);
                setAddToZone(null);
              }}
            >
              {tr(definition.labelKey, definition.labelDefault)}
            </button>
          ))}
        </div>
      </RingilDialog>
    </div>
  );
};

const DropLine: React.FC<{ horizontal: boolean }> = ({ horizontal }) => (
  <div className={clsx('absolute bg-content-brand z-10 pointer-events-none', horizontal ? 'left-0 top-0 bottom-0 w-0.5' : 'left-0 right-0 top-0 h-0.5')} />
);

interface SegmentedProps<T> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

/** Selected segment is a filled button, the rest outlined — the house style for toggles. */
function Segmented<T extends string | number>({ label, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div className="flex flex-row items-center gap-2">
      <span className="text-xs text-content-secondary">{label}</span>
      {options.map(option => (
        <RingilButton
          key={String(option.value)}
          label={option.label}
          mode={option.value === value ? 'filled-rectangle' : 'outlined-rectangle'}
          size="small"
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}
